import { LoadingOutlined, InfoCircleOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Row,
  Select,
  Spin,
  Switch,
  TreeSelect,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GetPerfCycleTopLevelApi } from "../../api/perf-cycles/GetPerfCycleTopPlanApi";
import CascadeTopPlanApi from "../../api/plans/CascadeTopPlanApi";
import GetCascadeTreeApi from "../../api/units/GetCascadeTreeApi";
import { GetUnitPositionsApi2 } from "../../api/units/GetUnitPositionsApi2";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import PageLoader from "../sub/PageLoader";
import { GetEvaluationsApi } from "../../api/ehrm/GetEvaluationsApi";
import { GetEmployeeEvaluationsApi } from "../../api/ehrm/GetEmployeeEvaluationsApi";
import { GetGoalGroupsApi } from "../../api/ehrm/GetGoalGroupsApi";
import PhrmService from "../../services/PhrmService";

const { Option } = Select;

const { TextArea } = Input;

function CascadePlanModal(props) {
  const [submit, setSubmit] = useState(false);
  const [unitsTree, setUnitsTree] = useState([]);
  const [unitPositions, setUnitPositions] = useState([]);
  const [topPlan, setTopPlan] = useState();

  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const positionState = useSelector((state) => state.positionState.value);
  const [form] = Form.useForm();
  const [evaluations, setEvaluations] = useState([]);
  const [goalGroups, setGoalGroups] = useState([]);
  const [selectedEvalId, setSelectedEvalId] = useState(null);
  const [assignedUser, setAssignedUser] = useState(null);
  const [isPhrmEnabled, setIsPhrmEnabled] = useState(false);
  const [fieldLabels, setFieldLabels] = useState({});
  const [cascadeFromSplitPlans, setCascadeFromSplitPlans] = useState(false);
  const [cascadeFromTasks, setCascadeFromTasks] = useState(false);
  const [isPercentageUpdate, setIsPercentageUpdate] = useState(false);
  const [isNegativeScale, setIsNegativeScale] = useState(false);

  function handleCancel() {
    setSubmit(false);
    setUnitsTree([]);
    setUnitPositions([]);
    setTopPlan();
    setIsPercentageUpdate(false);
    setIsNegativeScale(false);
    setCascadeFromSplitPlans(false);
    setCascadeFromTasks(false);
    form.resetFields();
    props.handleCancel();
  }

  useEffect(() => {
    if (props.visible) {
      getTopPlanDetails();
      getUnitsTree();

      checkPhrmPermissions();
      // Robust boolean conversion for isPercentageUpdate
      const isPercentageUpdateValue = Boolean(
        props?.plan?.isPercentageUpdate === true || 
        props?.plan?.isPercentageUpdate === 1 || 
        props?.plan?.isPercentageUpdate === "1" ||
        props?.plan?.isPercentageUpdate === "true"
      );
      
      // Robust boolean conversion for isNegativeScale
      const isNegativeScaleValue = Boolean(
        props?.plan?.isNegativeScale === true || 
        props?.plan?.isNegativeScale === 1 || 
        props?.plan?.isNegativeScale === "1" ||
        props?.plan?.isNegativeScale === "true"
      );
      
      form.setFields([
        {
          name: "title",
          value: props?.plan?.title,
        },
        {
          name: "isPercentageUpdate",
          value: isPercentageUpdateValue,
        },
        {
          name: "isNegativeScale",
          value: isNegativeScaleValue,
        },
      ]);
      
      // Set initial states from parent plan
      setIsPercentageUpdate(isPercentageUpdateValue);
      setIsNegativeScale(isNegativeScaleValue);
    }
  }, [props.visible]);

  async function checkPhrmPermissions() {
    const phrmEnabled = await PhrmService.isPhrmEnabled();
    setIsPhrmEnabled(phrmEnabled);
    setFieldLabels(PhrmService.getFieldLabels(phrmEnabled));
  }

  async function getTopPlanDetails() {
    const apiResponse = await GetPerfCycleTopLevelApi(perfCycleState.id);
    if (apiResponse.success) {
      setTopPlan(apiResponse.data);
    }
  }

  async function getUnitsTree() {
    const apiResponse = await GetCascadeTreeApi(props.plan?.unit?.unitId);
    if (apiResponse.message === "success") {
      setUnitsTree(apiResponse.units);
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    let attributesValues = [];
    topPlan?.planLevelAttributes?.forEach((attribute) => {
      let attributeValue = values[attribute.id];
      
      // Handle dropdown values - skip empty arrays
      if (attribute.valueType === "dropdown") {
        if (Array.isArray(attributeValue) && attributeValue.length === 0) {
          // Skip empty dropdown selections
          return;
        }
      }
      
      // Skip undefined or null values for optional attributes
      if (attributeValue === undefined || attributeValue === null || attributeValue === "") {
        return;
      }
      
      attributesValues.push({
        attributeId: attribute.id,
        value: attributeValue,
      });
    });

    if (isTargetValid(values.target)) {
      // Only send EHRM fields if PHRM is enabled and fields are filled
      const shouldSendToEhrm = isPhrmEnabled && 
        values.ehrmEvalId && 
        values.ehrmGoalTypeId && 
        values.ehrmGoalWeightage;

      const apiResponse = await CascadeTopPlanApi(
        props.plan?.planId,
        values.title,
        values.description || "",
        values.unit,
        values.target,
        values.targetDate || "",
        values.assignTo,
        positionState?.id,
        attributesValues,
        null,
        shouldSendToEhrm ? values.ehrmEvalId : undefined,
        shouldSendToEhrm ? values.ehrmGoalTypeId : undefined,
        shouldSendToEhrm ? values.ehrmGoalWeightage : undefined,
        values.cascadeFromSplitPlans || false,
        isPercentageUpdate,
        isNegativeScale
      );
      if (apiResponse.statusCode) {
        openNotificationError("bottomRight", apiResponse.message);
      } else if (apiResponse.message === "success") {
        openNotificationSuccess("bottomRight", `${topPlanState} cascaded`);
        props.handleOk(apiResponse.data);
      } else {
        openNotificationError(
          "bottomRight",
          "Couldn't add plan. Try again later."
        );
      }
      handleCancel();
    } else {
      form.setFields([
        {
          name: "target",
          errors: [`cannot exceed more than ${props.plan?.cascadeLimit}`],
        },
      ]);
      setSubmit(false);
    }
  }

  async function selectAssignTo(positionId) {
    const position = unitPositions.find(p => p.id === positionId);
    
    if (position && position.user) {
      setAssignedUser(position.user);
      
      form.setFieldsValue({
        empReference: position.user.empReferrenceId || '',
      });
      
      if (position.user.empReferrenceId) {
        const evalResponse = await GetEmployeeEvaluationsApi(position.user.empReferrenceId);
        if (evalResponse.ok && evalResponse.data) {
          setEvaluations(evalResponse.data);
        } else {
          setEvaluations([]);
        }
      } else {
        setEvaluations([]);
      }
    } else {
      setAssignedUser(null);
      setEvaluations([]);
      form.setFieldsValue({
        empReference: '',
      });
    }
    
    form.setFieldsValue({
      ehrmEvalId: undefined,
      ehrmGoalTypeId: undefined,
      ehrmGoalWeightage: undefined,
    });
    setSelectedEvalId(null);
    setGoalGroups([]);
  }

  async function selectEvaluation(evalId) {
    setSelectedEvalId(evalId);
    
    const goalGroupResponse = await GetGoalGroupsApi(evalId);
    if (goalGroupResponse.ok && goalGroupResponse.data) {
      setGoalGroups(goalGroupResponse.data);
    } else {
      setGoalGroups([]);
    }
    
    form.setFieldsValue({
      ehrmGoalTypeId: undefined,
    });
  }

  async function selectUnit(value) {
    const apiResponse = await GetUnitPositionsApi2(value);
    if (apiResponse.success) {
      setUnitPositions(apiResponse.data);
      form.setFields([
        {
          name: "assignTo",
          value: "",
        },
      ]);
    }
  }

  function isTargetValid(value) {
    if (!props.plan?.canExceed) {
      if (value > props.plan.cascadeLimit) {
        return false;
      }
    }
    return true;
  }



  const unitSelector = (
    <Form.Item name="unitScale" noStyle>
      <Select disabled style={{ width: 100 }}>
        <Option value={props.plan?.scale?.scaleUnitAbbreviation}>
          {props.plan?.scale?.scaleUnitAbbreviation}
        </Option>
      </Select>
    </Form.Item>
  );

  function renderAttributes() {
    let output = [];

    topPlan?.planLevelAttributes?.forEach((attribute) => {
      output.push(
        <Col span={12} key={attribute.id}>
          <Form.Item label={attribute.name} name={attribute.id}>
            {attribute.valueType === "text" ? (
              <Input style={{ width: "100%" }} placeholder={attribute.name} />
            ) : attribute.valueType === "number" ? (
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder={attribute.name}
              />
            ) : attribute.valueType === "double" ? (
              <InputNumber
                min={attribute.minValue || 0}
                max={attribute.maxValue || undefined}
                step={attribute.decimalPrecision ? 1 / Math.pow(10, attribute.decimalPrecision) : 0.01}
                precision={attribute.decimalPrecision || 2}
                style={{ width: "100%" }}
                placeholder={attribute.name}
              />
            ) : attribute.valueType === "dropdown" ? (
              <Select
                mode="multiple"
                showSearch
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%", maxWidth: "300px" }}
                getPopupContainer={(trigger) => trigger.parentNode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {attribute.dropdownOptions?.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.value}
                  </Option>
                ))}
              </Select>
            ) : attribute.valueType === "date" ? (
              <DatePicker style={{ width: "100%" }} placeholder={attribute.name} />
            ) : (
              <Input style={{ width: "100%" }} placeholder={attribute.name} />
            )}
          </Form.Item>
        </Col>
      );
    });

    return output;
  }

  return (
    <>
      <Modal
        title={`Cascade ${topPlanState}`}
        visible={props.visible}
        onCancel={() => handleCancel()}
        footer={null}
        destroyOnClose={true}
        width={900}
      >
        <div className="mb-2 text-base">
          {`Cascade from: ${props?.plan?.title}`}
        </div>

        <Form
          name="cascadePlan"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          initialValues={{
            planGroup: props.plan?.planGroup,
            unitScale: props.plan?.scale?.scaleUnitAbbreviation,
            title: props?.plan?.title,
          }}
          form={form}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Plan Group*" name="planGroup">
                <Input disabled placeholder="Plan group" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={fieldLabels.title || "Title*"}
                name="title"
                // initialValue={`${props?.plan?.title}`}
                rules={[
                  {
                    required: true,
                    message: `Please enter title!`,
                  },
                ]}
              >
                <Input placeholder={topPlanState} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Unit*"
                name="unit"
                rules={[
                  {
                    required: true,
                    message: "Please select a unit!",
                  },
                ]}
              >
                <TreeSelect
                  showSearch
                  treeLine={true}
                  dropdownStyle={{
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                  treeData={unitsTree}
                  placeholder="Select unit"
                  treeDefaultExpandAll
                  treeNodeFilterProp={"title"}
                  showIcon={false}
                  filterTreeNode
                  onChange={(value) => selectUnit(value)}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Assign to*"
                name="assignTo"
                rules={[
                  {
                    required: true,
                    message: "Please select a position!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select position"
                  optionFilterProp="children"
                  onChange={(value) => selectAssignTo(value)}
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {unitPositions.map((position) => {
                    return (
                      <Option key={position.id} value={position.id}>
                        {(position.user?.firstName
                          ? position.user?.firstName + ", "
                          : "") + position.designation?.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>



            <Col span={12}>
              <Form.Item
                label={fieldLabels.target || "Target*"}
                name="target"
                rules={[
                  {
                    required: true,
                    message: "Please enter target!",
                  },
                ]}
              >
                <InputNumber
                  addonBefore={isNegativeScale ? "-" : null}
                  addonAfter={
                    props.plan?.scale?.scaleName === "Number"
                      ? props.plan?.scale?.scaleName === "Percentage" && "%"
                      : unitSelector
                  }
                  style={{ width: "100%" }}
                  formatter={(value) => {
                    if (!value) return '';
                    // Format number with commas for better readability
                    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                  }}
                  parser={(value) => {
                    if (!value) return '';
                    // Remove commas when parsing
                    return value.replace(/,/g, '');
                  }}
                />
              </Form.Item>
            </Col>

            {topPlan?.target !== undefined && topPlan?.target !== null && (
              <Col span={6}>
                <Form.Item label=" " name="isPercentageUpdate" valuePropName="checked">
                  <div className="flex items-center">
                    <Checkbox
                      checked={isPercentageUpdate}
                      disabled={isNegativeScale} // Disable when negative scale is enabled
                      onChange={(e) => setIsPercentageUpdate(e.target.checked)}
                    >
                      Update by %
                    </Checkbox>

                    <Popover
                      placement="topRight"
                      content={
                        <div className="text-xs">When enabled, progress updates will be entered as percentages (0-100%)</div>
                      }
                      trigger="hover"
                    >
                      <div className="text-gray-500 text-xs ml-1">
                        <InfoCircleOutlined />
                      </div>
                    </Popover>
                  </div>
                </Form.Item>
              </Col>
            )}

            {topPlan?.target !== undefined && topPlan?.target !== null && (
              <Col span={6}>
                <Form.Item label=" " name="isNegativeScale" valuePropName="checked">
                  <div className="flex items-center">
                    <Checkbox
                      checked={isNegativeScale}
                      disabled={true} // Always disabled - inherited from parent
                      onChange={(e) => setIsNegativeScale(e.target.checked)}
                    >
                      Negative Scale
                    </Checkbox>

                    <Popover
                      placement="topRight"
                      content={
                        <div className="text-xs">
                          Enables countdown logic for loss reduction, debt paydown, and cost cutting scenarios. 
                          Progress shows remaining amount instead of achieved amount.
                          <br /><br />
                          <strong>Inherited from parent plan - cannot be changed.</strong>
                        </div>
                      }
                      trigger="hover"
                    >
                      <div className="text-gray-500 text-xs ml-1">
                        <InfoCircleOutlined />
                      </div>
                    </Popover>
                  </div>
                </Form.Item>
              </Col>
            )}

            {topPlan?.description && (
              <Col span={24}>
                <Form.Item label={fieldLabels.description || "Description"} name="description">
                  <TextArea
                    showCount
                    maxLength={100}
                    placeholder="Description"
                  />
                </Form.Item>
              </Col>
            )}

            {topPlan?.targetDate && (
              <Col span={12}>
                <Form.Item label="Target date*" name="targetDate">
                  <DatePicker className="w-full" placeholder="Target date" />
                </Form.Item>
              </Col>
            )}

            {topPlan?.cascadeFromSplitPlansEnabled && (
              <Col span={24}>
                <Form.Item
                  label="Cascade from Split Plans"
                  name="cascadeFromSplitPlans"
                  valuePropName="checked"
                  tooltip="When enabled, split plans will contribute to this plan's progress instead of cascade plans"
                >
                  <Switch
                    disabled={cascadeFromTasks}
                    onChange={(val) => setCascadeFromSplitPlans(val)}
                  />
                </Form.Item>
              </Col>
            )}

            {topPlan?.cascadeFromTasksEnabled && (
              <Col span={24}>
                <Form.Item
                  label="Calculate Progress from Tasks"
                  name="cascadeFromTasks"
                  valuePropName="checked"
                  tooltip="When enabled, task completion will drive this plan's progress"
                >
                  <Switch
                    disabled={cascadeFromSplitPlans}
                    onChange={(val) => setCascadeFromTasks(val)}
                  />
                </Form.Item>
              </Col>
            )}

            {isPhrmEnabled && (
              <>
                <Col span={12}>
                  <Form.Item
                    label="Employee Reference"
                    name="empReference"
                  >
                    <Input 
                      placeholder="Auto-filled from assigned user" 
                      disabled
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Evaluation"
                    name="ehrmEvalId"
                    rules={[{ required: false }]}
                  >
                    <Select
                      placeholder="Select evaluation"
                      onChange={(value) => selectEvaluation(value)}
                      allowClear
                      disabled={!assignedUser?.empReferrenceId}
                    >
                      {evaluations.map((evaluation) => (
                        <Option key={evaluation.EVAL_ID} value={evaluation.EVAL_ID}>
                          {evaluation.EVAL_NAME}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Goal Group"
                    name="ehrmGoalTypeId"
                    rules={[{ required: false }]}
                  >
                    <Select 
                      placeholder="Select goal group" 
                      allowClear
                      disabled={!selectedEvalId}
                    >
                      {goalGroups.map((group) => (
                        <Option key={group.GGRP_ID} value={group.GGRP_ID}>
                          {group.GGRP_NAME}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Goal Weight (%)"
                    name="ehrmGoalWeightage"
                    rules={[{ required: false }]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      precision={2}
                      placeholder="0.00"
                      style={{ width: '100%' }}
                      disabled={!assignedUser?.empReferrenceId}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {renderAttributes()}

            {!topPlan && <PageLoader />}

            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className=" flex justify-end">
                  <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                    Cancel
                  </Button>
                  <Button
                    className="btn w-40"
                    type="primary"
                    htmlType={submit && "submit"}
                    disabled={submit}
                  >
                    {submit ? (
                      <Spin
                        size="small"
                        indicator={
                          <LoadingOutlined style={{ color: "#fff" }} spin />
                        }
                      />
                    ) : (
                      `Cascade`
                    )}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default CascadePlanModal;
